import { useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, LinearProgress, MenuItem, Paper, Stack,
  TablePagination, TextField, Typography,
} from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { useIsGreek } from "../context/LanguageContext";
import useAdminUserMonitoring from "../hooks/useAdminUserMonitoring";
import { mapPhaseLabel } from "./feasibility/simulationProgress";

const STATUS = {
  pending: ["Σε αναμονή", "Queued", "warning"],
  running: ["Εκτελείται", "Running", "info"],
  completed: ["Ολοκληρώθηκε", "Completed", "success"],
  failed: ["Απέτυχε", "Failed", "error"],
  cancelled: ["Ακυρώθηκε", "Cancelled", "default"],
};

function timestamp(value, greek) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleString(greek ? "el-GR" : "en-GB", { dateStyle: "short", timeStyle: "medium" })
    : "—";
}

function duration(job) {
  if (job?.run_time) return job.run_time;
  if (job?.elapsed_seconds == null) return "—";
  const seconds = Math.max(0, Math.floor(job.elapsed_seconds));
  return [Math.floor(seconds / 3600), Math.floor(seconds / 60) % 60, seconds % 60]
    .map(value => String(value).padStart(2, "0")).join(":");
}

function Status({ job, greek }) {
  const item = STATUS[job.status] || ["Άγνωστη κατάσταση", "Unknown status", "default"];
  return <Chip size="small" variant="outlined" color={item[2]}
    label={job.cancel_requested ? (greek ? "Ζητήθηκε ακύρωση" : "Cancellation requested") : item[greek ? 0 : 1]} />;
}

function RunDetails({ job, greek }) {
  const text = (el, en) => greek ? el : en;
  if (!job) return <Typography variant="body2" color="text.secondary">{text("Δεν υπάρχει καταγεγραμμένη εκτέλεση.", "No recorded run.")}</Typography>;
  return <Stack spacing={0.75} sx={{ minWidth: 0 }}>
    <Stack direction="row" useFlexGap flexWrap="wrap" gap={1} alignItems="center">
      <Typography variant="body2" fontWeight={700} sx={{ overflowWrap: "anywhere" }}>{job.project_name}</Typography>
      <Status job={job} greek={greek} />
    </Stack>
    <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={2} rowGap={0.25}>
      <Typography variant="caption" color="text.secondary">{text("Υποβολή", "Submitted")}: {timestamp(job.requested_at, greek)}</Typography>
      <Typography variant="caption" color="text.secondary">{text("Έναρξη", "Started")}: {timestamp(job.started_at, greek)}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {job.status === "running" ? text("Χρόνος έως τώρα", "Elapsed") : text("Διάρκεια", "Duration")}: {duration(job)}
      </Typography>
    </Stack>
    {job.phase && <Typography variant="caption" color="text.secondary">
      {text("Στάδιο", "Stage")}: {mapPhaseLabel(job.phase, greek)}
    </Typography>}
    {job.status === "running" && <Box>
      <LinearProgress variant={job.progress == null ? "indeterminate" : "determinate"} value={job.progress ?? 0}
        aria-label={text("Πρόοδος εκτέλεσης", "Run progress")} />
      <Typography variant="caption" color="text.secondary">
        {job.progress == null ? text("Δεν έχει αναφερθεί ποσοστό προόδου", "Progress percentage not reported") : `${job.progress}%`}
        {" · "}{text("Τελευταίο σήμα λειτουργίας", "Last worker signal")}: {timestamp(job.heartbeat_at, greek)}
      </Typography>
    </Box>}
    {job.heartbeat_stale && <Typography variant="caption" color="warning.main">
      {text("Δεν υπάρχει πρόσφατο σήμα λειτουργίας. Η κατάσταση χρειάζεται έλεγχο.", "No recent worker signal. The run needs checking.")}
    </Typography>}
    {job.error && <Box component="details" sx={{ fontSize: 12, color: "error.main", overflowWrap: "anywhere" }}>
      <Box component="summary" sx={{ cursor: "pointer", fontWeight: 600 }}>{text("Λεπτομέρειες σφάλματος", "Error details")}</Box>
      <Typography component="pre" variant="caption" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", m: 0, mt: 0.5 }}>{job.error}</Typography>
    </Box>}
  </Stack>;
}

const rowSx = {
  display: "grid",
  gridTemplateColumns: { xs: "36px minmax(0, 1fr)", md: "44px minmax(180px, 1fr) 72px 150px minmax(0, 2fr)" },
  columnGap: 2, rowGap: 1, px: 2, py: 1.5, alignItems: "center",
  "& > *": { minWidth: 0 },
  "& > :nth-child(n+3)": { gridColumn: { xs: "2", md: "auto" } },
};

export default function AdminUserMonitoring() {
  const greek = useIsGreek();
  const text = (el, en) => greek ? el : en;
  const { data, error, loading, refresh } = useAdminUserMonitoring();
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [activePage, setActivePage] = useState(0);
  const filtered = useMemo(() => (data?.users || []).filter(user => {
    const matches = [user.name, user.email, user.id].some(value => String(value || "").toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()));
    return matches && (scope !== "active" || user.running + user.queued > 0);
  }), [data, search, scope]);
  const safePage = Math.min(page, Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1));
  const active = data?.active_jobs || [];
  const safeActivePage = Math.min(activePage, Math.max(0, Math.ceil(active.length / 5) - 1));
  const forbidden = error?.status === 401 || error?.status === 403;
  const totals = data?.counts;
  const paginationLabel = type => ({
    next: text("Επόμενη σελίδα", "Next page"),
    previous: text("Προηγούμενη σελίδα", "Previous page"),
    first: text("Πρώτη σελίδα", "First page"),
    last: text("Τελευταία σελίδα", "Last page"),
  }[type]);

  return <Stack spacing={2} sx={{ minWidth: 0 }}>
    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1} alignItems={{ sm: "center" }}>
      <Box>
        <Typography variant="h6" fontWeight={700}>{text("Παρακολούθηση μελετών", "Study monitoring")}</Typography>
        <Typography variant="body2" color="text.secondary">
          {text("Αυτόματη ανανέωση ανά 5 δευτερόλεπτα όσο η καρτέλα είναι ορατή.", "Refreshes every 5 seconds while this tab is visible.")}
        </Typography>
        <Typography variant="caption" color={error ? "warning.main" : "text.secondary"}>
          {data ? `${text("Τελευταία ενημέρωση", "Last updated")}: ${timestamp(data.captured_at, greek)}` : text("Αναμονή δεδομένων", "Waiting for data")}
        </Typography>
      </Box>
      <Button variant="outlined" size="small" startIcon={<RefreshOutlinedIcon />} onClick={refresh} disabled={loading}>
        {text("Ανανέωση", "Refresh")}
      </Button>
    </Stack>
    {error && <Alert severity={forbidden ? "error" : "warning"}>
      {forbidden
        ? text("Η πρόσβαση στην παρακολούθηση απαιτεί ενεργή σύνδεση διαχειριστή.", "Monitoring requires an active administrator session.")
        : data
          ? text("Η ενημέρωση απέτυχε. Εμφανίζονται τα τελευταία διαθέσιμα στοιχεία, όχι ζωντανά δεδομένα.", "Refresh failed. The last available snapshot is shown, not live data.")
          : text("Δεν ήταν δυνατή η φόρτωση της παρακολούθησης. Δοκιμάστε ξανά.", "Monitoring could not be loaded. Try again.")}
    </Alert>}
    {loading && !data && <LinearProgress aria-label={text("Φόρτωση παρακολούθησης", "Loading monitoring")} />}
    {data && <>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" }, gap: 1.5 }}>
        {[
          [text("Εκτελούνται", "Running"), totals.running],
          [text("Στην ουρά", "Queued"), totals.queued],
          [text("Χρήστες με ενεργά αιτήματα", "Users with active requests"), totals.active_users],
          [text("Αποθηκευμένα έργα", "Saved projects"), totals.projects],
        ].map(([label, value]) => <Paper key={label} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          <Typography variant="h5" fontWeight={700}>{value}</Typography>
        </Paper>)}
      </Box>
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ p: 1.75, bgcolor: "action.hover" }}>
          <Typography variant="subtitle2" fontWeight={700}>{text("Τρέχουσες εκτελέσεις και αναμονές", "Running and queued studies")}</Typography>
          <Typography variant="caption" color="text.secondary">{text("Αιτήματα που εκτελούνται ή έχουν υποβληθεί και περιμένουν να ξεκινήσουν.", "Requests being processed or submitted and waiting to start.")}</Typography>
        </Box>
        {active.length ? active.slice(safeActivePage * 5, safeActivePage * 5 + 5).map(job => <Box key={job.id}
          sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0,1fr)", md: "minmax(0,1fr) minmax(0,3fr)" }, gap: 1.5, p: 1.75, borderTop: 1, borderColor: "divider" }}>
          <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
            <Typography variant="body2" fontWeight={700}>{job.user_name || job.email || job.user_id}</Typography>
            <Typography variant="caption" color="text.secondary">{job.email}</Typography>
          </Box>
          <RunDetails job={job} greek={greek} />
        </Box>) : <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          {text("Δεν υπάρχουν εκτελέσεις σε εξέλιξη ή στην ουρά.", "No studies are running or queued.")}
        </Typography>}
        {active.length > 5 && <TablePagination component="div" count={active.length} page={safeActivePage} rowsPerPage={5}
          getItemAriaLabel={paginationLabel}
          rowsPerPageOptions={[]} onPageChange={(_, value) => setActivePage(value)}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${text("από", "of")} ${count}`} />}
      </Paper>
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} sx={{ p: 1.75 }}>
          <TextField size="small" label={text("Αναζήτηση χρήστη ή email", "Search user or email")} value={search}
            onChange={event => { setSearch(event.target.value); setPage(0); }} sx={{ flex: 1, minWidth: 0 }} />
          <TextField select size="small" label={text("Προβολή", "Show")} value={scope}
            onChange={event => { setScope(event.target.value); setPage(0); }} sx={{ minWidth: { sm: 230 } }}>
            <MenuItem value="all">{text("Όλοι οι χρήστες", "All users")}</MenuItem>
            <MenuItem value="active">{text("Με ενεργά αιτήματα", "With active requests")}</MenuItem>
          </TextField>
        </Stack>
        <Box role="table" aria-label={text("Παρακολούθηση ανά χρήστη", "Monitoring by user")}>
          <Box role="row" sx={{ ...rowSx, display: { xs: "none", md: "grid" }, bgcolor: "action.hover" }}>
            {[text("Α/Α", "No."), text("Χρήστης", "User"), text("Έργα", "Projects"), text("Ενεργά αιτήματα", "Active requests"), text("Τελευταία καταγεγραμμένη εκτέλεση", "Latest recorded run")].map(label =>
              <Typography key={label} role="columnheader" variant="caption" fontWeight={700}>{label}</Typography>)}
          </Box>
          {filtered.slice(safePage * rowsPerPage, (safePage + 1) * rowsPerPage).map((user, index) => <Box role="row" key={user.id} sx={{
            ...rowSx, borderTop: 1, borderColor: "divider",
            bgcolor: index % 2 ? "action.hover" : "background.paper",
            "&:hover": { bgcolor: "action.selected" },
          }}>
            <Typography role="cell" variant="caption" color="text.secondary" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", alignSelf: { xs: "start", md: "center" }, pt: { xs: 0.25, md: 0 } }}>
              {safePage * rowsPerPage + index + 1}
            </Typography>
            <Box role="cell" sx={{ overflowWrap: "anywhere" }}>
              <Typography variant="body2" fontWeight={700}>{user.name || user.email || user.id}</Typography>
              <Typography variant="caption" color="text.secondary">{user.email}</Typography>
            </Box>
            <Box role="cell" sx={{ fontSize: 13 }}>
              <Box component="span" sx={{ display: { md: "none" }, color: "text.secondary" }}>{text("Έργα", "Projects")}: </Box>
              <Box component="span" sx={{ display: "inline-block", minWidth: 32, px: 0.75, py: 0.25, borderRadius: 1, bgcolor: "action.hover", textAlign: "center", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{user.project_count}</Box>
            </Box>
            <Stack role="cell" spacing={0.5} sx={{ maxWidth: 150 }}>
              {[[text("Εκτελούνται", "Running"), user.running, "info.main"], [text("Αναμονή", "Queued"), user.queued, "warning.main"]].map(([label, count, color]) =>
                <Stack key={label} direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="caption" sx={{ fontWeight: count ? 700 : 400, color: count ? color : "text.secondary", fontVariantNumeric: "tabular-nums" }}>{count}</Typography>
                </Stack>)}
            </Stack>
            <Stack role="cell" spacing={0.75} sx={{ borderLeft: { md: 1 }, borderColor: { md: "divider" }, pl: { md: 2 } }}>
              <RunDetails job={user.latest_attempt} greek={greek} />
              {user.last_completed && user.latest_attempt?.status !== "completed" && <Typography variant="caption" color="text.secondary">
                {text("Προηγούμενη ολοκληρωμένη", "Previous completed study")}: {user.last_completed.project_name}
                {" · "}{timestamp(user.last_completed.completed_at || user.last_completed.started_at, greek)}
                {" · "}{text("Διάρκεια", "Duration")}: {user.last_completed.run_time || "—"}
              </Typography>}
            </Stack>
          </Box>)}
        </Box>
        {!filtered.length && <Typography sx={{ p: 2 }} variant="body2">{text("Δεν βρέθηκαν χρήστες.", "No users found.")}</Typography>}
        <TablePagination component="div" count={filtered.length} page={safePage} rowsPerPage={rowsPerPage}
          getItemAriaLabel={paginationLabel}
          onPageChange={(_, value) => setPage(value)} rowsPerPageOptions={[50, 100, 200]}
          onRowsPerPageChange={event => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
          labelRowsPerPage={text("Ανά σελίδα", "Rows per page")}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${text("από", "of")} ${count}`}
          sx={{ "& .MuiTablePagination-toolbar": { flexWrap: "wrap", px: 1.5 }, "& .MuiTablePagination-spacer": { flex: 1 } }} />
      </Paper>
      <Typography variant="caption" color="text.secondary">
        {text("Τα έργα αφορούν όσα είναι αποθηκευμένα στην πλατφόρμα. Εμφανίζεται η τελευταία διαθέσιμη κατάσταση ανά έργο, όχι πλήρες ιστορικό όλων των εκτελέσεων. Η παύλα σημαίνει ότι το στοιχείο δεν έχει καταγραφεί.",
          "Project counts include projects saved on the platform. This shows the latest available state per project, not a complete history of every run. A dash means the value was not recorded.")}
      </Typography>
    </>}
  </Stack>;
}
